// src/components/ResultCard.jsx
import React, { useState } from 'react';
import { ExternalLink, Copy, Check, ChevronDown, ChevronUp } from 'lucide-react';

const isUrl = (str) =>
  typeof str === 'string' && (str.startsWith('http://') || str.startsWith('https://'));

const CARD_STATUS = (result) => {
  if (!result)        return { label: 'Pending',   cls: 'card-missing', dotCls: '',        badgeCls: 'badge-muted'    };
  if (result.error)   return { label: 'Error',     cls: 'card-error',   dotCls: 'error',   badgeCls: 'badge-warning'  };
  if (result.found)   return { label: result.manual ? 'Reference' : 'Found', cls: 'card-found',   dotCls: 'found',   badgeCls: 'badge-success'  };
  return               { label: 'Not Found', cls: 'card-missing', dotCls: 'missing', badgeCls: 'badge-danger'   };
};

const CopyButton = ({ value }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(String(value)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <button
      onClick={handleCopy}
      className={`copy-btn visible ${copied ? 'copied' : ''}`}
      title="Copy value"
    >
      {copied ? <Check size={10} /> : <Copy size={10} />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

const ResultCard = ({ tool, result, index }) => {
  const [expanded, setExpanded] = useState(false);
  const { label, cls, dotCls, badgeCls } = CARD_STATUS(result);
  const hasData = result?.found && result?.data && Object.keys(result.data).length > 0;

  return (
    <div
      className={`animate-fade-in-up glass ${cls}`}
      style={{
        animationDelay: `${Math.min(index * 35, 300)}ms`,
        borderRadius: 'var(--radius-md)',
        padding: '14px 16px',
        cursor: hasData ? 'pointer' : 'default',
        transition: 'background 150ms, transform 150ms',
        userSelect: 'none',
      }}
      onClick={() => hasData && setExpanded((v) => !v)}
      onMouseEnter={(e) => { if (hasData) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
    >
      {/* Card header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ fontSize: 24, flexShrink: 0, lineHeight: 1 }}>{tool.icon}</div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>{tool.name}</h3>
            <span className="badge badge-muted">{tool.category}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
            {dotCls && <span className={`status-dot ${dotCls}`} />}
            <span className={`badge ${badgeCls}`}>{label}</span>
          </div>
        </div>

        {/* Expand toggle */}
        {hasData && (
          <div style={{ color: 'var(--color-text-muted)', flexShrink: 0 }}>
            {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
          </div>
        )}
      </div>

      {/* Expanded data */}
      {result && expanded && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--color-border)' }}>
          {result.error ? (
            <p style={{ margin: 0, fontSize: 12, color: 'var(--color-amber)', fontStyle: 'italic' }}>
              {result.error}
            </p>
          ) : result.found ? (
            tool.name === 'AI Web Report' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {Object.entries(result.data).map(([key, value]) => (
                  <div key={key}>
                    <h4 style={{ margin: '0 0 8px 0', fontSize: 13, color: 'var(--color-accent)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{key}</h4>
                    {Array.isArray(value) ? (
                      <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                        {value.map((item, i) => (
                          <li key={i} style={{ marginBottom: 4 }}>{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <p style={{ margin: 0, fontSize: 12, color: 'var(--color-text-secondary)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                        {String(value)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {Object.entries(result.data).map(([key, value]) => (
                  <div key={key} className="result-row">
                    <span style={{
                      minWidth: 130, fontSize: 10, fontWeight: 600,
                      color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em',
                      flexShrink: 0,
                    }}>
                      {key}
                    </span>
                    {isUrl(value) ? (
                      <a
                        href={value}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          color: 'var(--color-accent)', fontSize: 12,
                          display: 'flex', alignItems: 'center', gap: 4,
                          textDecoration: 'none', flex: 1, minWidth: 0,
                        }}
                      >
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {value.length > 60 ? `${value.slice(0, 60)}…` : value}
                        </span>
                        <ExternalLink size={11} style={{ flexShrink: 0 }} />
                      </a>
                    ) : (
                      <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', flex: 1, wordBreak: 'break-all' }}>
                        {String(value)}
                      </span>
                    )}
                    <CopyButton value={value} />
                  </div>
                ))}
              </div>
            )
          ) : (
            <p style={{ margin: 0, fontSize: 12, color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
              No digital footprint detected on this platform.
            </p>
          )}
        </div>
      )}

      {/* Show hint if found but not expanded */}
      {hasData && !expanded && (
        <p style={{ margin: '8px 0 0 36px', fontSize: 11, color: 'var(--color-text-muted)' }}>
          {Object.keys(result.data).length} field{Object.keys(result.data).length !== 1 ? 's' : ''} — click to expand
        </p>
      )}
    </div>
  );
};

// ── Platform metadata helpers ─────────────────────────────────────────────

export function getIconForPlatform(name) {
  const icons = {
    'GitHub':               '💻',
    'Reddit':               '🤖',
    'Dev.to':               '💻',
    'IDCrawl':              '🆔',
    'Sherlock Project':     '🕵️',
    'Twitter/X':            '🐦',
    'Instagram':            '📸',
    'LinkedIn':             '💼',
    'Email Format Check':   '✅',
    'Have I Been Pwned':    '🔒',
    'Hunter.io':            '🎯',
    'WHOIS Lookup':         '🌐',
    'Security Analysis':    '🔒',
    'Google Reverse Image': '🔍',
    'TinEye':               '👁️',
    'Social-Searcher':      '🔎',
    'HackerNews':           '🟠',
    'Pinterest':            '📌',
    'Gravatar':             '👤',
    'Twitch':               '🎮',
    'Steam':                '🎮',
    'YouTube':              '▶️',
    'TikTok':               '🎵',
    'Spotify':              '🎵',
    'AI Web Report':        '🧠',
  };
  return icons[name] || '🔍';
}

export function getCategoryFromResult(name) {
  const cats = {
    'GitHub':               'Development',
    'Reddit':               'Social',
    'Dev.to':               'Development',
    'IDCrawl':              'Aggregator',
    'Sherlock Project':     'Tool',
    'Twitter/X':            'Social',
    'Instagram':            'Social',
    'LinkedIn':             'Professional',
    'Email Format Check':   'Validation',
    'Have I Been Pwned':    'Security',
    'Hunter.io':            'Verification',
    'WHOIS Lookup':         'Registration',
    'Security Analysis':    'Security',
    'Google Reverse Image': 'Search',
    'TinEye':               'Search',
    'Social-Searcher':      'Multi-Platform',
    'HackerNews':           'Development',
    'Pinterest':            'Social',
    'Gravatar':             'Identity',
    'Twitch':               'Gaming',
    'Steam':                'Gaming',
    'YouTube':              'Video',
    'TikTok':               'Social',
    'Spotify':              'Music',
    'AI Web Report':        'Analysis',
  };
  return cats[name] || 'General';
}

export default ResultCard;
